import React from 'react';
import { connect } from 'react-redux';
import {
  ApplicationState,
  AppUserAdminActionCreators,
  AppUserAdminState,
  AppState
} from '../../../store';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { RouteComponentProps } from 'react-router';
import MUIDataTable, {
  SelectableRows,
  MUIDataTableColumnOptions,
  MUIDataTableMeta
} from 'mui-datatables';
import { ScaleLoader } from 'react-spinners';
import { IconButton, Box, Switch, Button, Tooltip } from '@material-ui/core';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { IAppUserService } from '../../../services/Interfaces/IAppUserService';
import { AppUser } from '../../../interfaces/AppUser';
import { withSnackbar, ProviderContext } from 'notistack';
import { Organization } from '../../../interfaces/Organization';
import moment from 'moment';
import { MUITranslations } from '../../../helpers/MUITableTranslations';

type UsersTabProps = {
  appUserAdminState: AppUserAdminState;
  appState: AppState;
} & ProviderContext &
  typeof AppUserAdminActionCreators &
  RouteComponentProps;

interface IUsersTabState {
  users: AppUser[];
  isLoading: boolean;
}
class UsersTab extends React.PureComponent<UsersTabProps, IUsersTabState> {
  private translatorService!: ITranslatorService;
  private appUserService!: IAppUserService;

  static contextType = ApplicationContext;

  state = {
    users: [],
    isLoading: false
  } as IUsersTabState;

  public componentDidMount() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadUsers();
      }
    );
  }

  loadUsers = async () => {
    const userList = await this.appUserService.GetAppUsers();

    this.setState({
      users: userList.users,
      isLoading: false
    });
  };

  handleUserActiveChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    tableMeta: MUIDataTableMeta
  ) => {
    const id = tableMeta.rowData[0];
    const usersClone = [...this.state.users];
    const updUser = usersClone.find((item) => item.id === id);

    if (updUser !== undefined) {
      updUser.isActive = event.target.checked;

      try {
        await this.appUserService.UpdateUser(updUser);
        this.setState({
          users: usersClone
        });
        this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
          variant: 'success'
        });
      } catch (error) {
        this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
          variant: 'error'
        });
      }
    }
  };

  onRowClick = (e: any, id: string) => {
    this.props.history.push('/admin/users/' + id);
  };

  getColumns = () => {
    const columns = [
      {
        name: 'id',
        options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
      },
      {
        name: 'email',
        label: this.translatorService.Tranlate('USERS_EMAIL_HEADER', 'Email'),
        options: {
          filter: false,
          customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
            return (
              <Button
                color="primary"
                className=" text-primary"
                onClick={(e) => this.onRowClick(e, tableMeta.rowData[0])}
              >
                <span>{value}</span>
              </Button>
            );
          }
        } as MUIDataTableColumnOptions
      },
      {
        name: 'lastName',
        label: this.translatorService.Tranlate('USERS_LAST_NAME_HEADER', 'Nume'),
        options: {
          filter: false
        }
      },
      {
        name: 'firstName',
        label: this.translatorService.Tranlate('USERS_FIRST_NAME_HEADER', 'Prenume'),
        options: {
          filter: false
        }
      },
      {
        name: 'organization',
        label: this.translatorService.Tranlate('USERS_ORGANIZATION_HEADER', 'Organizatie'),
        options: {
          customBodyRender: (value: Organization) => {
            return value ? value.hoName : '';
          }
        } as MUIDataTableColumnOptions
      },
      {
        name: 'updatedByUserFullName',
        label: this.translatorService.Tranlate('USERS_UPDATEDBY_HEADER', 'Modificat de')
      },
      {
        name: 'dateModification',
        label: this.translatorService.Tranlate('USERS_DATE_MODIFICATION_HEADER', 'Data MOdificare'),

        options: {
          sortDirection: 'desc',
          customBodyRender: (value: Date) =>
            moment.utc(value).local().format(this.props.appState.longDateFormat)
        } as MUIDataTableColumnOptions
      },
      {
        name: 'isActive',
        label: this.translatorService.Tranlate('USERS_IS_ACTIVE_HEADER', 'Activ'),
        options: {
          filterOptions: {
            names: [
              this.translatorService.Tranlate('TABLE_IS_ACTIVE_COLUMN_ACTIVE', 'Activ'),
              this.translatorService.Tranlate('TABLE_IS_ACTIVE_COLUMN_INACTIVE', 'Inactiv')
            ],
            logic(isActive: any, filterVal: string[]) {
              const show =
                (filterVal.indexOf(this.names[0]) !== -1 && isActive === true) ||
                (filterVal.indexOf(this.names[1]) !== -1 && isActive === false);
              return !show;
            }
          },
          customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
            return (
              <Switch
                checked={value}
                onChange={(e) => this.handleUserActiveChange(e, tableMeta)}
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            );
          }
        }
      }
    ];

    return columns;
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.appUserService = (this.context as AppContext).appUserService;

    const options = {
      filter: true,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      // responsive: "stacked",
      customToolbar: () => {
        return (
          <Tooltip title={this.translatorService.Tranlate('Add', 'Adauga')}>
            <IconButton
              aria-label="add"
              color="secondary"
              className="m-2"
              onClick={() => {
                this.props.history.push('/admin/users/new');
              }}
            >
              <AddCircleTwoToneIcon />
            </IconButton>
          </Tooltip>
        );
      }
    };

    return (
      <Box m={1} p={1}>
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
        </div>
        {!this.state.isLoading ? (
          <MUIDataTable
            title={''}
            data={this.state.users}
            columns={this.getColumns()}
            options={options}
          />
        ) : null}
      </Box>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appUserAdminState: state.appUserAdmin,
    appState: state.app
  }),
  AppUserAdminActionCreators // Selects which action creators are merged into the component's props
)(withSnackbar(UsersTab as any));
